import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, MailCheck, Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const RESEND_COOLDOWN = 30;

const VerifyModal = ({ open, setOpen, page, onSuccess }) => {
    const [step, setStep] = useState("email");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [expiry, setExpiry] = useState(0);
    const [cooldown, setCooldown] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setExpiry((v) => (v > 0 ? v - 1 : 0));
            setCooldown((v) => (v > 0 ? v - 1 : 0));
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, []);

    const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

    const requestOtp = async (e) => {
        e?.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/access/request", { email, page });
            setStep("otp");
            setOtp("");
            setExpiry(data.expires_in || 600);
            setCooldown(RESEND_COOLDOWN);
            toast.success("Verification code sent to your email");
        } catch (err) {
            setError(formatApiError(err, "Could not send code. Try again."));
        } finally {
            setLoading(false);
        }
    };

    const verify = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await api.post("/access/verify", { email, otp, page });
            toast.success("Email verified — welcome!");
            setOpen(false);
            onSuccess();
        } catch (err) {
            const status = err?.response?.status;
            setError(formatApiError(err, "Verification failed"));
            if (status === 410 || status === 429) setOtp("");
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-cyan-500/60";

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="border-white/10 bg-[#0D1424] text-white sm:max-w-md" data-testid="verify-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <ShieldCheck className="h-5 w-5 text-cyan-400" /> Verify Your Email
                    </DialogTitle>
                    <DialogDescription className="text-slate-400">
                        {step === "email"
                            ? "This section is protected. Enter your email and we'll send you a one-time verification code."
                            : `We sent a 6-digit code to ${email}. It expires in ${fmt(expiry)}.`}
                    </DialogDescription>
                </DialogHeader>

                {step === "email" ? (
                    <form onSubmit={requestOtp} className="space-y-4">
                        <input
                            type="email"
                            required
                            data-testid="verify-email-input"
                            className={inputCls}
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {error && <p className="text-sm text-red-400" data-testid="verify-error">{error}</p>}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="verify-send-otp-button"
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />}
                            Send Verification Code
                        </button>
                    </form>
                ) : (
                    <form onSubmit={verify} className="space-y-4">
                        <input
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            maxLength={6}
                            required
                            autoFocus
                            data-testid="verify-otp-input"
                            className={`${inputCls} text-center font-mono2 text-2xl tracking-[0.5em]`}
                            placeholder="••••••"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        />
                        {expiry === 0 && (
                            <p className="text-sm text-amber-400" data-testid="verify-expired">Code expired — please resend a new one.</p>
                        )}
                        {error && <p className="text-sm text-red-400" data-testid="verify-error">{error}</p>}
                        <button
                            type="submit"
                            disabled={loading || otp.length !== 6}
                            data-testid="verify-submit-button"
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                            Verify & Unlock
                        </button>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                            <button type="button" onClick={() => setStep("email")} className="hover:text-cyan-300" data-testid="verify-change-email">
                                ← Change email
                            </button>
                            <button
                                type="button"
                                onClick={requestOtp}
                                disabled={cooldown > 0 || loading}
                                data-testid="verify-resend-button"
                                className="flex items-center gap-1 hover:text-cyan-300 disabled:opacity-50"
                            >
                                <RefreshCw className="h-3 w-3" />
                                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                            </button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};

export const VerifyGate = ({ page, title, teaser, children }) => {
    const [state, setState] = useState("loading"); // loading | locked | unlocked
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    const loadProtected = useCallback(async () => {
        try {
            const { data } = await api.get(`/protected/${page}`);
            setData(data);
            setState("unlocked");
        } catch {
            setState("locked");
        }
    }, [page]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/access/status");
                if (data.pages?.includes(page)) await loadProtected();
                else setState("locked");
            } catch {
                setState("locked");
            }
        })();
    }, [page, loadProtected]);

    if (state === "loading")
        return (
            <div className="flex min-h-[60vh] items-center justify-center" data-testid={`${page}-gate-loading`}>
                <Loader2 className="h-7 w-7 animate-spin text-cyan-500" />
            </div>
        );

    if (state === "locked")
        return (
            <div className="relative mx-auto max-w-4xl px-4 py-24 sm:px-6" data-testid={`${page}-locked-preview`}>
                {/* blurred teaser skeleton */}
                <div className="pointer-events-none select-none space-y-4 opacity-40 blur-sm" aria-hidden="true">
                    <div className="h-10 w-2/3 rounded-lg bg-slate-700/50" />
                    <div className="h-4 w-full rounded bg-slate-800/60" />
                    <div className="h-4 w-5/6 rounded bg-slate-800/60" />
                    <div className="h-4 w-4/6 rounded bg-slate-800/60" />
                    <div className="mt-8 grid grid-cols-3 gap-4">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="h-28 rounded-xl bg-slate-800/60" />
                        ))}
                    </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center px-4">
                    <div className="glow-card w-full max-w-md rounded-3xl p-8 text-center sm:p-10">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-cyan-500/30 bg-cyan-500/10">
                            <Lock className="h-7 w-7 text-cyan-400" />
                        </div>
                        <h1 className="mt-5 font-display text-2xl font-extrabold text-white sm:text-3xl" data-testid={`${page}-locked-title`}>
                            {title}
                        </h1>
                        <p className="mt-3 text-sm leading-relaxed text-slate-400">{teaser}</p>
                        <button
                            onClick={() => setOpen(true)}
                            data-testid={`${page}-verify-button`}
                            className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20 transition-transform hover:scale-[1.03]"
                        >
                            <MailCheck className="h-4 w-4" /> Verify Email to View
                        </button>
                        <p className="mt-4 text-xs text-slate-600">Free instant verification · code valid for 10 minutes</p>
                    </div>
                </div>
                <VerifyModal open={open} setOpen={setOpen} page={page} onSuccess={loadProtected} />
            </div>
        );

    return children(data);
};
